import { useState } from "react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MapPin, Copy, Check, ExternalLink } from "lucide-react";

export default function GoogleMapsCard() {
  const { user, refresh } = useAuth();
  const [url, setUrl] = useState(user?.google_maps_url || "");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const batisurProfile = `${window.location.origin}/artisans/${user.id}`;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.patch("/users/me", { google_maps_url: url });
      toast.success(url ? "Fiche Google associée ✅" : "Lien retiré");
      try { await refresh?.(); } catch (_e) { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const copyProfile = async () => {
    try { await navigator.clipboard.writeText(batisurProfile); } catch (_e) { /* ignore */ }
    setCopied(true);
    toast.success("Lien Batisur copié — collez-le dans le champ « Site web » de votre fiche Google !");
    setTimeout(() => setCopied(false), 2500);
  };

  const findMyListing = () => {
    const q = encodeURIComponent(`${user.company || user.name} ${user.city || ""}`.trim());
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank");
    toast.info("Vérifiez que c'est bien votre fiche, puis copiez le lien depuis la barre d'adresse et collez-le ici.");
  };

  return (
    <div className="mb-8 border-2 border-[#0A0A0B] bg-white" data-testid="google-maps-card">
      <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center gap-2">
        <MapPin className="w-5 h-5 text-[#FF4D00]" />
        <div>
          <div className="display text-xl">Ma fiche Google Maps</div>
          <div className="text-xs text-zinc-600">
            Associez votre fiche : un badge « Voir notre fiche Google ★ » apparaît sur votre profil public Batisur.
          </div>
        </div>
      </div>
      <form onSubmit={save} className="p-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={findMyListing}
          className="btn-batisur text-xs shrink-0"
          data-testid="google-maps-find"
        >
          🔍 Trouver ma fiche automatiquement
        </button>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://maps.app.goo.gl/… (Fiche Google → Partager → Copier le lien)"
          className="flex-1 min-w-[260px] border-2 border-[#0A0A0B] px-3 py-2 text-sm"
          data-testid="google-maps-input"
        />
        <button type="submit" disabled={busy} className="btn-anthracite text-xs disabled:opacity-60" data-testid="google-maps-save">
          {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
        {user?.google_maps_url && (
          <a href={user.google_maps_url} target="_blank" rel="noreferrer"
            className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] underline flex items-center gap-1"
            data-testid="google-maps-view">
            <ExternalLink className="w-3 h-3" /> Voir ma fiche
          </a>
        )}
      </form>
      <div className="mx-5 mb-5 border-2 border-dashed border-[#FF4D00] bg-orange-50 p-4 flex flex-wrap items-center gap-3" data-testid="google-maps-reminder">
        <div className="flex-1 min-w-[240px] text-sm text-zinc-800">
          <strong>💡 Le réflexe qui rapporte :</strong> mettez votre lien Batisur dans le champ
          <strong> « Site web »</strong> de votre fiche Google Maps — chaque visiteur local arrive
          directement sur votre profil sécurisé (devis, badges, avis).
        </div>
        <button onClick={copyProfile} type="button" className="btn-batisur text-xs flex items-center gap-1.5 shrink-0" data-testid="google-maps-copy-profile">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copié !" : "Copier mon lien Batisur"}
        </button>
      </div>
    </div>
  );
}
